import { BillingFrequency, Pricing } from '../../../contracts';
import { getFormattedNumber } from '../../../utils/get-formatted-number';
import { SubscriptionsSummaryContext } from '../../../providers/summary-provider-utils';

export interface SubscriptionDisclaimerData {
  renewalPeriodKey: string;
  amount: string;
  taxesKey: string;
}

// The pay button must not render without this data (ROSCA/FTC), so the
// disclaimer and the button are gated on the same source.
export function getSubscriptionDisclaimerData(
  subscriptionData: SubscriptionsSummaryContext | undefined,
  isTaxExcludedCountry: boolean
): SubscriptionDisclaimerData | undefined {
  const renewalPeriodKey = getRenewalPeriodKey(subscriptionData?.plan?.billingFrequency);
  const priceBreakdown = subscriptionData?.pricing?.priceBreakdown;
  // Where tax is added on top of the price (US/CA) the renewal amount is quoted
  // pre-tax as "excluded taxes"; elsewhere tax is part of the total, quoted as
  // "included taxes".
  const amount = getFormattedPriceText(
    subscriptionData?.pricing,
    isTaxExcludedCountry ? priceBreakdown?.baseNet : priceBreakdown?.priceTotal
  );
  const taxesKey = isTaxExcludedCountry ? 'taxesExcluded' : 'taxesIncluded';

  return renewalPeriodKey && amount ? { renewalPeriodKey, amount, taxesKey } : undefined;
}

// Translation keys under the `checkout` namespace, worded per the legal
// template (ROSCA/FTC): "renew [annually/monthly]".
const BILLING_FREQUENCY_TO_RENEWAL_KEY_MAP: Record<BillingFrequency, string> = {
  [BillingFrequency.DAILY]: 'renewalPeriodDaily',
  [BillingFrequency.WEEKLY]: 'renewalPeriodWeekly',
  [BillingFrequency.MONTHLY]: 'renewalPeriodMonthly',
  [BillingFrequency.YEARLY]: 'renewalPeriodAnnually',
};

export function getRenewalPeriodKey(frequency: BillingFrequency | undefined) {
  return frequency ? BILLING_FREQUENCY_TO_RENEWAL_KEY_MAP[frequency] : undefined;
}

export function getFormattedPriceText(pricing: Pricing | undefined, priceValue: number | undefined) {
  if (!pricing || priceValue === undefined) {
    return undefined;
  }
  const formattedNumber = getFormattedNumber(priceValue, pricing);
  const space = pricing.spacing ? ' ' : '';
  return pricing.symbolPosition === 'Left'
    ? `${pricing.currencySymbol}${space}${formattedNumber}`
    : `${formattedNumber}${space}${pricing.currencySymbol}`;
}
